import { useEffect, useRef } from 'react';
import { MessageSquare, User } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import type { CoachNote } from '@shared/schema';
import { trackEvent } from '@/lib/analytics/posthog';

interface CoachNotesDisplayProps {
  notes: CoachNote[];
  targetType: 'video' | 'plan';
  targetId: number;
  title?: string;
  className?: string;
}

export function CoachNotesDisplay({
  notes,
  targetType,
  targetId,
  title = "Coach's Notes",
  className = '',
}: CoachNotesDisplayProps) {
  // Filter notes by targetType and targetId
  const filteredNotes = notes.filter((note) => {
    return note.targetType === targetType && note.targetId === targetId;
  });

  // Track when player sees coach notes
  const trackedRef = useRef(false);
  useEffect(() => {
    if (filteredNotes.length > 0 && !trackedRef.current) {
      trackedRef.current = true;
      trackEvent('coach_notes_seen_by_player', {
        notes_count: filteredNotes.length,
        target_type: targetType,
      });
    }
  }, [filteredNotes.length, targetType]);

  // Don't render if no matching notes
  if (filteredNotes.length === 0) {
    return null;
  }

  const formatDate = (dateString: string | Date) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return 'Unknown date';
    }
  };

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="w-5 h-5 text-[var(--apple-blue)]" />
        <h4 className="text-lg font-semibold text-[var(--apple-blue)]">{title}</h4>
      </div>
      <div className="space-y-3">
        {filteredNotes.map((note) => (
          <Card
            key={note.id}
            className="glass-card dark:glass-card-dark rounded-2xl border-l-4 border-[var(--apple-blue)]"
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[var(--apple-blue)]/20 flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-[var(--apple-blue)]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                    From your coach - {formatDate(note.createdAt)}
                  </p>
                  <p className="text-gray-700 dark:text-gray-200 whitespace-pre-wrap">
                    {note.content}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
